import {transparentize} from 'polished';
import React, {FC, memo} from 'react';
import styled, {css} from 'styled-components';

import {
  FrontMouseEvent,
  Handler,
  HandlerOf,
} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon, IconName} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {UserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {
  ConversationStatusesEnum,
  UnreadStatusesEnum,
} from '../../../../../helpers/models/conversationHelpers';
import {CellsActionsEnum, InterfaceEventsEnum, trackInterfaceEvent} from '../../../../../services/tracking';

/*
 * Props.
 */

interface CellStatusProps {
  status: ConversationStatusesEnum;
  unreadStatus: UnreadStatusesEnum;
  isSelected?: boolean;
  toggleMarkAsRead: Handler;
  hasP2QuickActions: boolean;
  hasReminder?: boolean;
}

/*
 * Style.
 */

const StyledWrapperDiv = styled.div`
  grid-area: top-left;
  display: grid;
  position: relative;

  height: 100%;
  align-items: center;
  justify-items: center;

  &::after {
    content: '';
    position: absolute;
    top: -20%;
    right: -20%;
    bottom: -20%;
    left: -20%;
  }
`;

/*
 * Component.
 */

/** Middle-left status + unread indicator for a conversation. */
export const CellStatus: FC<CellStatusProps> = memo((props) => {
  // Extract relevant flags from our props.
  const {unreadStatus, toggleMarkAsRead, hasReminder, hasP2QuickActions} = props;

  // Find the information to display.
  const isUnread = unreadStatus !== UnreadStatusesEnum.NONE;
  const iconName = getConversationStatusIconName(props.status);

  // Derive our behavior from these flags.
  const shouldDisplayStatus = computeShouldDisplayStatus(hasP2QuickActions, iconName, hasReminder);
  const shouldAlwaysDisplayDot = !shouldDisplayStatus && isUnread;

  const onClick: HandlerOf<FrontMouseEvent> = (event) => {
    event.preventDefault();
    toggleMarkAsRead();
    trackInterfaceEvent(InterfaceEventsEnum.P2_CELL_CLICK, {
      action:
        unreadStatus === UnreadStatusesEnum.NONE ? CellsActionsEnum.MARK_READ : CellsActionsEnum.MARK_UNREAD,
    });
  };

  // Render the dot and the icon.
  return (
    <StyledWrapperDiv onClick={onClick}>
      {renderUnreadDot(props, shouldAlwaysDisplayDot)}
      {shouldDisplayStatus && iconName && renderIcon(props, iconName)}
    </StyledWrapperDiv>
  );
});

function computeShouldDisplayStatus(
  hasP2QuickActions: boolean,
  iconName: IconName | undefined,
  hasReminder: boolean | undefined,
) {
  if (hasP2QuickActions) {
    return false;
  }
  return Boolean(iconName) && !hasReminder;
}

/*
 * Unread dot.
 */

interface UnreadStyleProps {
  $shouldAlwaysDisplayDot?: boolean;
  $isSelected?: boolean;
  $unreadStatus: UnreadStatusesEnum;
}
const StyledUnreadDiv = styled.div<UnreadStyleProps>`
  grid-column: 1;
  grid-row: 1;

  opacity: ${(p) => (p.$shouldAlwaysDisplayDot ? 1 : 0)};
  width: 10px;
  height: 10px;
  border-radius: 50%;

  background-color: ${(p) =>
    getCellUnreadBackgroundColor(p.theme, p.$unreadStatus, p.$isSelected) ||
    transparentize(0.7, p.theme.palette.blue.shade40)};

  /* Hover state. */
  ${StyledWrapperDiv}:hover & {
    opacity: 1;
  }
`;

/** Get the color of the unread dot based on the unread status. */
function getCellUnreadBackgroundColor(
  theme: UserTheme,
  unreadStatus: UnreadStatusesEnum,
  isSelected?: boolean,
) {
  if (isSelected && unreadStatus !== UnreadStatusesEnum.NONE) {
    return theme.greys.white;
  }

  if (isSelected) {
    return theme.alphas.white40;
  }

  if (unreadStatus === UnreadStatusesEnum.MESSAGE_UNREAD) {
    return theme.palette.blue.shade40;
  }

  if (unreadStatus === UnreadStatusesEnum.SNOOZE_UNREAD) {
    return theme.palette.orange.shade40;
  }

  return undefined;
}

/** Render the unread status dot for the current conversation. */
function renderUnreadDot(props: CellStatusProps, shouldAlwaysDisplayDot: boolean) {
  // TODO: This should be a ChromelessButton for accessibility and tracking.
  return (
    <StyledUnreadDiv
      $shouldAlwaysDisplayDot={shouldAlwaysDisplayDot}
      $isSelected={props.isSelected}
      $unreadStatus={props.unreadStatus}
      data-testid="cell-unread-dot"
    />
  );
}

/*
 * Status icon.
 */

export interface IconStyleProps {
  $isSelected?: boolean;
  $hasP2QuickActions: boolean;
  $unreadStatus: UnreadStatusesEnum;
  $status: ConversationStatusesEnum;
}
export const StyledCellStatusIconDiv = styled.div<IconStyleProps>`
  color: ${(p) =>
    getConversationStatusIconColor(p.theme, p.$unreadStatus, p.$status, p.$hasP2QuickActions, p.$isSelected)};

  ${(p) => addAdditionalStyles(p)};
`;

function addAdditionalStyles(props: IconStyleProps) {
  if (props.$hasP2QuickActions) {
    return '';
  }

  return css`
    grid-column: 1;
    grid-row: 1;

    /* Hover state. */
    ${StyledWrapperDiv}:hover & {
      opacity: 0;
    }
  `;
}

/** Find the status icon color to display based on the unread status. */
export function getConversationStatusIconColor(
  theme: UserTheme,
  unreadStatus: UnreadStatusesEnum,
  status: ConversationStatusesEnum,
  hasP2QuickActions: boolean,
  isSelected?: boolean,
) {
  // Extract the values from the props.
  const isMessageUnread = unreadStatus === UnreadStatusesEnum.MESSAGE_UNREAD;
  const isSnoozeUnread = unreadStatus === UnreadStatusesEnum.SNOOZE_UNREAD;

  if (isSelected) {
    return theme.alphas.white50;
  }

  // Unread state.
  if (isMessageUnread && !hasP2QuickActions) {
    return theme.palette.blue.shade40;
  }

  if (isSnoozeUnread) {
    return theme.palette.orange.shade40;
  }

  if (status === ConversationStatusesEnum.ARCHIVED) {
    return theme.palette.green.shade40;
  }

  if (status === ConversationStatusesEnum.TRASHED) {
    return theme.palette.red.shade40;
  }

  if (status === ConversationStatusesEnum.SPAM) {
    return theme.palette.purple.shade40;
  }

  // Base state.
  return theme.greys.shade60;
}

export function getConversationStatusIconName(status: ConversationStatusesEnum): IconName | undefined {
  switch (status) {
    case ConversationStatusesEnum.ARCHIVED:
      return 'archiveFilledSmall';

    case ConversationStatusesEnum.TRASHED:
      return 'trashFilledSmall';

    case ConversationStatusesEnum.SPAM:
      return 'spamFilledSmall';

    default:
      return undefined;
  }
}

/** Render the status icon for the current conversation. */
function renderIcon(props: CellStatusProps, iconName: IconName) {
  return (
    <StyledCellStatusIconDiv
      $isSelected={props.isSelected}
      $status={props.status}
      $hasP2QuickActions={props.hasP2QuickActions}
      $unreadStatus={props.unreadStatus}
    >
      <Icon name={iconName} />
    </StyledCellStatusIconDiv>
  );
}
